/* eslint-disable camelcase */
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setGlobalAlert } from '../../../../actions/offer.actions';
import { INFLUENCERS_ENDPOINTS } from '../../../../config/api';
import Button from '../../../../shared/components/Button';
import { ACTIONS, CONTENT_STATUSES } from '../../../../utils/constants';
import { UserContentCards } from '../components/ContentApprovalComponents';
import ObjectAttributesSidePane from '../components/ObjectAttributesBox/ObjectAttributesSidePane';
import OtherDeliverablesSidePane from '../components/OtherDeliverablesSidePane';
import axios from 'axios';
import Pagination from '../../../../../frontend/components/components/utils/Pagination';

/**
 * @typedef OtherDeliverablesSectionParams
 * @property {import('../../../../utils/types/types').UserOffer} userOffer
 * @property {string} otherDeliverableInstructions
 *
 * @param {OtherDeliverablesSectionParams} params
 * @returns
 */
const OtherDeliverablesSection = ({
  userOffer,
  otherDeliverableInstructions,
}) => {
  const OTHER_DELIVERABLES_PER_PAGE = 20;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  /** @type {import('../../../../reducers/offer.reducer').Offer} */
  const offer = useSelector(store => get(store, 'offer.offer'));

  const [state, setState] = useState({
    otherDeliverablesPosts: [],

    hasOtherDeliverables: false,
    objectAttributeMetadata: {},
    objectAttributeValues: {},
    userPostId: null,
    otherDeliverablesSidePane: {
      render: false,
      open: false,
      post: null,
      status: null,
    },
    objectAttributesSidePane: {
      render: false,
      open: false,
      post: null,
      status: null,
    },
    totalPages: 0,
    currentPage: 1,
    otherDeliverablesTotalCount: 0,
    loading: false
  });

  useEffect(() => { loadOtherDeliverables(state.currentPage) }, [userOffer]);

  const changePage = useCallback((page) => { loadOtherDeliverables(page); }, [state]);

  const loadOtherDeliverables = (page) => {
    setState({ ...state, loading: true });

    axios.get(
      INFLUENCERS_ENDPOINTS.PAGINATED_USER_POST.GET(
        get(offer, 'user_offer.id', null),
        { page, platforms: ['other'] }
      )
    ).then(({ data }) => {
      const {
        total_pages,
        user_posts,
        total_count
      } = data;

      let otherDeliverablesPostData = user_posts.filter(up => up.platform === 'other');
      if (userOffer.rejection_notes) {
        otherDeliverablesPostData = otherDeliverablesPostData.map(other => ({
          ...other,
          am_review_status: CONTENT_STATUSES.REJECTED
        }));
      }

      setState({
        ...state,
        loading: false,
        otherDeliverablesPosts: otherDeliverablesPostData,
        currentPage: page,
        otherDeliverablesTotalCount: total_count,
        totalPages: total_pages
      });
    }).catch((e) => {
      setState({ ...state, loading: false });
    });
  };

  const onEditContent = useCallback((draft, status) => {
    toggleSidePane({
      open: true,
      post: draft,
      status,
      sidePane: status === 'edit_draft' ? 'otherDeliverablesSidePane' : 'objectAttributesSidePane'
    });
  });

  /**
     * @param {boolean} open If side pane is open or not
     * @param {boolean} render If side pane should render or not,
     * this is for when the side pane is created but not opened
     */
  const toggleSidePane = useCallback(({
    open,
    render = true,
    success = undefined,
    post = null,
    status,
    action = ACTIONS.CREATE,
    sidePane = 'otherDeliverablesSidePane'
  }) => {
    document.body.style.overflow = open ? 'hidden' : 'unset';

    let objectAttributeValues = {};
    let objectAttributeMetadata = [];
    let userPostId = null;
    if (sidePane === 'objectAttributesSidePane') {
      if (get(post, 'user_post_object_attributes')) {
        Object.keys(post.user_post_object_attributes)
          .forEach(key => {
            const entry = post.user_post_object_attributes[key];
            objectAttributeValues[key] = entry.form_values;
          });
        objectAttributeMetadata = post.object_attribute_metadata;
        userPostId = post.id;
      } else if (render) {
        // Keeps state for visualization
        objectAttributeMetadata = state.objectAttributeMetadata;
        objectAttributeValues = state.objectAttributeValues;
        userPostId = state.userPostId;
      }
    }

    setState({
      ...state,
      [sidePane]: {
        open,
        render,
        post,
        status,
      },
      objectAttributeValues,
      objectAttributeMetadata,
      userPostId,
    });
    if (success !== undefined) {
      document.querySelector('body').scrollTo({ top: 0, behavior: 'smooth' });
      let alert = null;
      let variant = 'success';
      if (success) {
        switch (action) {
          case ACTIONS.CREATE:
            alert = <React.Fragment>
              <i className="icon fal fa-check-circle"></i>
              {t('influencers.pages.campaign.other_deliverables.saved_successfully')}
            </React.Fragment>;
            break;
          case ACTIONS.DELETE:
            alert = <React.Fragment>
              <i className="icon fal fa-check-circle"></i>
              {t('influencers.pages.campaign.other_deliverables.deleted_successfully')}
            </React.Fragment>;
            break;
          case ACTIONS.UPDATE:
            alert = <React.Fragment>
              <i className="icon fal fa-check-circle"></i>
              {t('influencers.pages.campaign.other_deliverables.updated_successfully')}
            </React.Fragment>;
            break;
        }
      } else {
        alert = <React.Fragment>
          <i className="icon fal fa-exclamation-circle"></i>
          {t('influencers.pages.campaign.other_deliverables.saved_error')}
        </React.Fragment>;
        variant = 'danger';
      }
      dispatch(setGlobalAlert({
        content: alert,
        variant,
        shouldUpdateOffer: true
      }));
    }
  }, [state]);

  return (
    <React.Fragment>
      <div className="mb-5" dangerouslySetInnerHTML={{ __html: otherDeliverableInstructions }} />
      <Row className="justify-content-end">
        <div className='col-auto mb-4'>
          <Button variant='primary'
            onClick={() => toggleSidePane({
              open: true,
              sidePane: 'otherDeliverablesSidePane'
            })}>
            {t('influencers.pages.campaign.submit_deliverables')}
          </Button>
        </div>
      </Row>
      {
        state.otherDeliverablesPosts.length > 0 ?
        <React.Fragment>
          <UserContentCards
            userContent={state.otherDeliverablesPosts}
            onEdit={onEditContent}
            userContentType="userPost"
          />
          {
            state.otherDeliverablesTotalCount > OTHER_DELIVERABLES_PER_PAGE &&
            <Col xs="12" className="text-center mt-4">
              <Pagination
                currentPage={state.currentPage}
                lastPage={state.totalPages}
                fetchData={changePage}
              />
            </Col>
          }
        </React.Fragment>:
        <Card className="empty mt-5 mb-4-5">{t('influencers.pages.campaign.no_content')}</Card>
      }
      {state.otherDeliverablesSidePane.render && <OtherDeliverablesSidePane
        open={state.otherDeliverablesSidePane.open}
        post={state.otherDeliverablesSidePane.post}
        status={state.otherDeliverablesSidePane.status}
        onRequestClose={() => toggleSidePane({
          open: false,
          draft: state.otherDeliverablesSidePane.post,
          sidePane: 'otherDeliverablesSidePane'
        })}
        onAfterClose={(success, action = ACTIONS.CREATE) => toggleSidePane({
          open: false,
          render: false,
          success,
          action,
          sidePane: 'otherDeliverablesSidePane'
        })}
      />}
      {state.objectAttributesSidePane.render
        ? <ObjectAttributesSidePane
          open={state.objectAttributesSidePane.open}
          onRequestClose={() =>
            toggleSidePane({
              open: false,
              draft: state.objectAttributesSidePane.post,
              sidePane: 'objectAttributesSidePane'
            })
          }
          onAfterClose={(success, action = ACTIONS.UPDATE) =>
            toggleSidePane({
              open: false,
              render: false,
              success,
              action,
              sidePane: 'objectAttributesSidePane'
            })
          }
          objectAttributeMetadata={state.objectAttributeMetadata}
          objectAttributeValues={state.objectAttributeValues}
          objectAttributeEndpoint={INFLUENCERS_ENDPOINTS.OFFERS.USER_POSTS.PATCH(offer.id, state.userPostId)}
          updateType="user_post" />
        : null}
    </React.Fragment>
  );
};

OtherDeliverablesSection.propTypes = {
  userOffer: PropTypes.object.isRequired,
  otherDeliverableInstructions: PropTypes.string.isRequired,
};

export default OtherDeliverablesSection;
